/**
 * VÉRAULT Portal — Advisor: Planning Command Center (Pass B Task #4)
 *
 * The cross-journey OPERATIONAL DASHBOARD. This is NOT a competing editing
 * workspace — the actual planning/editing happens INSIDE each Journey. This
 * page is the monitoring + visibility layer:
 *
 *   · Needs attention   — overdue items + imminent journeys with blockers
 *   · Awaiting client   — intake not back · agreement unsigned · approval pending
 *   · Awaiting advisor  — missing itinerary · missing bookings · stalled drafts
 *   · Imminent          — departures within 30 days
 *   · In flight         — every active journey, the calm long list
 *
 * Every row deep-links into the Journey's Planning tab — that's where the
 * actual work happens.
 *
 * Single source: GET /api/planning/dashboard.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass,
  Sparkles,
  AlertTriangle,
  Clock,
  Users,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
} from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import { PlanningStudioLibrary } from "./PlanningStudioLibrary";

const { fadeInUp, stagger } = portalMotion;

const FILTERS = [
  { id: "needs_attention", label: "Needs attention", icon: AlertTriangle, tone: "terracotta" },
  { id: "awaiting_client", label: "Awaiting client", icon: Clock, tone: "sage" },
  { id: "awaiting_advisor", label: "Awaiting you", icon: Users, tone: "gold" },
  { id: "imminent", label: "Imminent", icon: CalendarClock, tone: "gold" },
  { id: "in_flight", label: "All in flight", icon: Compass, tone: "neutral" },
];

const BLOCKER_LABELS = {
  travelers: "Travelers",
  dates: "Dates",
  destination: "Destination",
  intake_complete: "Intake",
  estimate: "Estimate",
  client_approval: "Client approval",
  agreement_signed: "Agreement",
  bookings_complete: "Bookings",
  itinerary: "Itinerary",
};

function stageTone(stage) {
  const s = (stage || "").toLowerCase();
  if (s === "approved" || s === "booking_in_progress" || s === "trip_confirmed") return "gold";
  if (s === "in_travel" || s === "completed") return "sage";
  return "neutral";
}

function humanStage(stage) {
  return (stage || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AdvisorPlanning() {
  // Wave 31 · Track B — Planning Studio shell.  Two top-level modes:
  //   1. Operations dashboard (cross-trip monitor — pre-existing).
  //   2. Library (new — destinations, experiences, day templates,
  //      favorites, recently used, archive, search).
  const [studioMode, setStudioMode] = React.useState("operations");

  if (studioMode === "library") {
    return (
      <div className="space-y-6">
        <StudioModeBar mode={studioMode} onChange={setStudioMode} />
        <PlanningStudioLibrary />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <StudioModeBar mode={studioMode} onChange={setStudioMode} />
      <OperationsDashboard />
    </div>
  );
}

function StudioModeBar({ mode, onChange }) {
  const tabs = [
    { id: "operations", label: "Operations Dashboard" },
    { id: "library", label: "Library + Templates" },
  ];
  return (
    <div
      className="inline-flex rounded-full border border-[hsl(var(--surface-hairline))]/50 p-1 bg-[hsl(var(--background-paper))]/40"
      data-testid="planning-studio-mode-bar-top"
    >
      {tabs.map((t) => {
        const active = mode === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            data-testid={`planning-studio-top-${t.id}`}
            className={`font-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-colors ${
              active
                ? "bg-white text-[hsl(var(--foreground))] shadow-sm"
                : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function OperationsDashboard() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState("needs_attention");

  React.useEffect(() => {
    let alive = true;
    api
      .get("/planning/dashboard")
      .then(({ data }) => {
        if (alive) setData(data);
      })
      .catch(() => alive && setError("Couldn't load the planning command center."));
    return () => {
      alive = false;
    };
  }, []);

  const counts = React.useMemo(() => {
    const c = {};
    FILTERS.forEach((f) => (c[f.id] = 0));
    if (!data?.rows) return c;
    data.rows.forEach((r) => {
      (r.buckets || []).forEach((b) => {
        if (c[b] != null) c[b]++;
      });
    });
    return c;
  }, [data]);

  // Default to the first non-empty bucket
  React.useEffect(() => {
    if (!data?.rows) return;
    const firstNonEmpty = FILTERS.find((f) => counts[f.id] > 0);
    if (firstNonEmpty && counts[filter] === 0) {
      setFilter(firstNonEmpty.id);
    }
  }, [data, counts]); // eslint-disable-line

  const rows = React.useMemo(() => {
    if (!data?.rows) return [];
    return data.rows
      .filter((r) => (r.buckets || []).includes(filter))
      .sort((a, b) => {
        if (a.days_until == null) return 1;
        if (b.days_until == null) return -1;
        return a.days_until - b.days_until;
      });
  }, [data, filter]);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Planning Studio · Operations"
          title="Every experience in flight, at a glance."
          lede="Each row deep-links into its Experience — the work itself happens there."
        />
      </motion.div>

      {/* Filter rail — 5 buckets with counts */}
      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded={false} className="overflow-hidden">
          <div
            className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-[rgba(0,0,0,0.05)] divide-y sm:divide-y-0"
            data-testid="planning-filter-rail"
          >
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const count = counts[f.id] || 0;
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`planning-filter-${f.id}`}
                  className={`relative text-left p-5 transition-colors ${
                    active ? "bg-white" : "hover:bg-white/60"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon
                      className={`size-4 ${active ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`}
                      strokeWidth={1.5}
                    />
                    <span
                      className={`font-mono text-[10.5px] uppercase tracking-[0.22em] ${
                        active ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))]"
                      }`}
                    >
                      {f.label}
                    </span>
                  </div>
                  <p
                    className={`mt-2 font-serif text-2xl ${
                      active ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-secondary))]"
                    }`}
                  >
                    {count}
                  </p>
                  {active && (
                    <span
                      className="absolute -bottom-px left-5 right-5 h-px bg-[hsl(var(--accent-gold))]"
                      aria-hidden
                    />
                  )}
                </button>
              );
            })}
          </div>
        </Surface>
      </motion.div>

      {/* Rows */}
      <motion.div variants={fadeInUp}>
        <EditorialRule
          label={
            !data
              ? "loading"
              : rows.length === 0
              ? "nothing in this bucket"
              : `${rows.length} ${rows.length === 1 ? "experience" : "experiences"}`
          }
        />
        <div className="mt-5">
          {error ? (
            <Empty title={error} icon={<AlertTriangle className="size-6" strokeWidth={1.25} />} />
          ) : !data ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <Empty
              title={
                filter === "needs_attention"
                  ? "Nothing needs your attention right now."
                  : filter === "awaiting_client"
                  ? "No experiences are waiting on a client."
                  : filter === "awaiting_advisor"
                  ? "No experiences are waiting on you."
                  : filter === "imminent"
                  ? "Nothing departs in the next 30 days. The board is calm."
                  : "No experiences are currently in flight."
              }
              hint="When something surfaces here, it'll be the first thing you see."
              icon={<CheckCircle2 className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]" data-testid="planning-rows">
                {rows.map((r) => (
                  <li key={r.trip_id}>
                    <Link
                      to={`/portal/advisor/journeys/${r.trip_id}?tab=planning`}
                      className="group flex items-start justify-between gap-4 px-5 sm:px-6 py-4 hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
                      data-testid={`planning-row-${r.trip_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="font-serif text-lg text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors">
                            {r.internal_trip_name}
                          </p>
                          <StatusTag tone={stageTone(r.lifecycle_stage)}>
                            {humanStage(r.lifecycle_stage)}
                          </StatusTag>
                          {r.days_until != null && r.days_until >= 0 && r.days_until <= 30 && (
                            <StatusTag tone={r.days_until <= 7 ? "terracotta" : "gold"}>
                              {r.days_until === 0 ? "today" : `${r.days_until}d out`}
                            </StatusTag>
                          )}
                          {r.overdue_payments > 0 && (
                            <StatusTag tone="terracotta">
                              {r.overdue_payments} overdue payment{r.overdue_payments === 1 ? "" : "s"}
                            </StatusTag>
                          )}
                        </div>
                        <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
                          {r.client_name || "—"}
                          {r.start_date && r.end_date && (
                            <>
                              {" · "}
                              {fmt.formatDateRange(r.start_date, r.end_date)}
                            </>
                          )}
                        </p>
                        {r.waiting_on?.length > 0 && (
                          <div
                            className="mt-2 flex flex-wrap items-center gap-1.5"
                            data-testid={`planning-row-${r.trip_id}-waiting`}
                          >
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                              waiting on
                            </span>
                            {r.waiting_on.slice(0, 5).map((w) => (
                              <span
                                key={w}
                                className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30 rounded-full px-2 py-0.5"
                              >
                                {BLOCKER_LABELS[w] || w}
                              </span>
                            ))}
                            {r.waiting_on_party_label && (
                              <span
                                className={`font-mono text-[10px] uppercase tracking-[0.18em] ml-2 ${
                                  r.waiting_on_party === "client"
                                    ? "text-[hsl(var(--accent-sage,170_18%_42%))]"
                                    : "text-[hsl(var(--foreground-muted))]"
                                }`}
                              >
                                · {r.waiting_on_party_label}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <ArrowRight
                        className="size-4 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))] mt-2 shrink-0"
                        strokeWidth={1.5}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded>
          <div className="flex items-start gap-3">
            <Sparkles className="size-4 text-[hsl(var(--accent-gold))] mt-0.5" strokeWidth={1.5} />
            <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))]">
              The command center is a monitoring layer. To shape a journey, open it — every operational tab
              (Itinerary, Logistics, Agreements, Travelers, Payments) lives inside the Journey itself.
            </p>
          </div>
        </Surface>
      </motion.div>
    </motion.div>
  );
}
